import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Search, ArrowLeft, Wifi, Loader2, Signal, Phone, MessageSquare, Database, Calendar, CreditCard, CheckCircle2, AlertTriangle } from "lucide-react";
import axios from "axios";
import { useT } from "@/i18n";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_d216ca66-0733-41eb-aefb-a68564ba10f0/artifacts/fn20k37z_FINAL%20Logo%20Newsky%20png.png";
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function DataBar({ used, total, percent, t }) {
  const color = percent > 90 ? "#ef4444" : percent > 70 ? "#f97316" : "#10b981";
  return (
    <div>
      <div className="flex justify-between items-end mb-3">
        <span className="text-sm text-slate-400">{t.consultar.dataUsage}</span>
        <span className="text-xl sm:text-2xl font-extrabold text-white">{used}GB / {total}GB</span>
      </div>
      <div className="w-full h-5 rounded-full overflow-hidden" style={{ background: "rgba(255,255,255,0.1)" }}>
        <motion.div initial={{ width: 0 }} animate={{ width: `${Math.min(percent, 100)}%` }} transition={{ duration: 1, ease: "easeOut" }}
          className="h-full rounded-full" style={{ background: `linear-gradient(90deg, ${color}, ${color}dd)`, boxShadow: `0 0 10px ${color}40` }} />
      </div>
      <div className="flex justify-between mt-2">
        <span className="text-sm sm:text-base font-semibold" style={{ color }}>{percent}% {t.consultar.used}</span>
        <span className="text-sm sm:text-base font-bold" style={{ color }}>{(total - used).toFixed(2)}GB {t.consultar.remaining}</span>
      </div>
    </div>
  );
}

function InfoCard({ icon: Icon, label, value, color }) {
  return (
    <div className="glass-card rounded-xl p-4 flex items-center gap-3">
      <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: `${color}15`, color }}><Icon className="w-5 h-5" /></div>
      <div><p className="text-xs text-slate-500">{label}</p><p className="text-sm font-bold text-white">{value}</p></div>
    </div>
  );
}

export default function ConsultarDados() {
  const t = useT();
  const [simNumber, setSimNumber] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!simNumber.trim()) return;
    setLoading(true); setError(""); setResult(null);
    try {
      const { data } = await axios.post(`${API}/consultar-sim`, { msn: simNumber });
      if (data.success) setResult(data);
      else setError(data.error || t.consultar.notFound);
    } catch (err) {
      setError(err.response?.data?.detail || t.consultar.error);
    }
    setLoading(false);
  };

  const statusColor = result?.status === "ACTIVE" ? "#10b981" : result?.status === "SUSPENDED" ? "#f97316" : "#ef4444";
  const statusLabel = result?.status === "ACTIVE" ? t.consultar.active : result?.status === "SUSPENDED" ? t.consultar.suspended : result?.status || "—";

  return (
    <div className="min-h-screen bg-[#020617] relative">
      <div className="hero-gradient-orb-1 -top-32 left-1/4 opacity-30" />
      <div className="hero-gradient-orb-2 bottom-0 right-1/4 opacity-20" />

      <header className="relative z-10 border-b border-white/10 bg-slate-950/80 backdrop-blur-xl">
        <div className="max-w-5xl mx-auto px-4 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2"><img src={LOGO_URL} alt="NewSky" className="h-10 w-auto" /></Link>
          <Link to="/" className="inline-flex items-center gap-2 text-sm text-slate-400 hover:text-white transition-colors"><ArrowLeft className="w-4 h-4" /> {t.footer.back}</Link>
        </div>
      </header>

      <div className="relative z-10 max-w-3xl mx-auto px-4 py-12 sm:py-16">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-center mb-10">
          <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-orange-500/20 to-red-500/10 border border-orange-500/20 flex items-center justify-center mx-auto mb-6">
            <Wifi className="w-10 h-10 text-orange-400" />
          </div>
          <h1 className="text-2xl sm:text-3xl font-bold text-white mb-4">{t.consultar.title}</h1>
          <p className="text-sm sm:text-base text-slate-400 max-w-lg mx-auto">{t.consultar.desc}</p>
        </motion.div>

        <form onSubmit={handleSubmit} className="max-w-md mx-auto mb-10">
          <div className="glass-card rounded-2xl p-6 sm:p-8">
            <label className="block text-sm font-medium text-slate-300 mb-3">{t.consultar.label}</label>
            <input type="text" value={simNumber} onChange={(e) => setSimNumber(e.target.value)} data-testid="sim-number-input"
              placeholder={t.consultar.placeholder}
              className="w-full px-4 py-3.5 rounded-xl bg-white/5 border border-white/10 text-white text-base placeholder-slate-600 focus:outline-none focus:border-orange-500/50 focus:ring-1 focus:ring-orange-500/30 transition-all mb-4" required />
            <button type="submit" disabled={loading} data-testid="consultar-btn"
              className="w-full inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-xl font-medium text-white btn-gradient text-base disabled:opacity-60">
              {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Search className="w-5 h-5" />}
              {loading ? t.consultar.consulting : t.consultar.button}
            </button>
          </div>
        </form>

        {error && (
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="max-w-md mx-auto mb-8">
            <div className="glass-card rounded-xl p-4 flex items-center gap-3 border border-red-500/20">
              <AlertTriangle className="w-5 h-5 text-red-400 flex-shrink-0" /><p className="text-sm text-red-400">{error}</p>
            </div>
          </motion.div>
        )}

        {result && (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
            <div className="glass-card rounded-2xl p-6 mb-6" style={{ borderLeft: `3px solid ${statusColor}` }}>
              <div className="flex items-center justify-between mb-5">
                <div>
                  <p className="text-xs text-slate-500 mb-1">{t.consultar.number}</p>
                  <p className="text-2xl font-bold text-white tracking-wide">{result.msisdn}</p>
                </div>
                <span className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-bold" style={{ background: `${statusColor}15`, color: statusColor, border: `1.5px solid ${statusColor}40` }}>
                  <CheckCircle2 className="w-4 h-4" /> {statusLabel}
                </span>
              </div>
              <DataBar used={result.data_used_gb} total={result.data_limit_gb} percent={result.usage_percent} t={t} />
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-6">
              <InfoCard icon={Database} label={t.consultar.plan} value={result.plan_code} color="#f97316" />
              <InfoCard icon={Signal} label={t.consultar.network} value={result.communication} color="#3b82f6" />
              <InfoCard icon={CreditCard} label={t.consultar.sim} value={result.card_size} color="#8b5cf6" />
              <InfoCard icon={Phone} label={t.consultar.voice} value={result.voice === "ON" ? t.consultar.active : "—"} color="#10b981" />
              <InfoCard icon={MessageSquare} label={t.consultar.sms} value={result.sms === "ON" ? t.consultar.active : "—"} color="#06b6d4" />
              <InfoCard icon={Calendar} label={t.consultar.planStart} value={result.plan_start} color="#f59e0b" />
            </div>

            <div className="glass-card rounded-2xl overflow-hidden">
              <div className="px-5 py-3 border-b border-white/5"><p className="text-sm font-bold text-white">{t.consultar.details}</p></div>
              <div className="divide-y divide-white/5">
                {[
                  [t.consultar.serialNumber, result.sn],
                  [t.consultar.activation, result.first_day],
                  [t.consultar.calls, result.call_flat === "70FLAT" ? t.consultar.callsUnlimited : result.call_flat],
                  [t.consultar.planStartDate, result.plan_start],
                  [t.consultar.planEndDate, result.plan_end === "-" ? t.consultar.noExpiry : result.plan_end],
                ].map(([label, value]) => (
                  <div key={label} className="flex justify-between px-5 py-3">
                    <span className="text-sm text-slate-500">{label}</span>
                    <span className="text-sm font-medium text-white">{value}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="text-center mt-8">
              <button onClick={() => { setResult(null); setSimNumber(""); setError(""); }}
                className="text-sm text-slate-400 hover:text-white transition-colors inline-flex items-center gap-1">
                <Search className="w-4 h-4" /> {t.consultar.newSearch}
              </button>
            </div>
          </motion.div>
        )}

        {!result && !error && !loading && (
          <p className="text-center text-xs text-slate-600">{t.consultar.simInfo}</p>
        )}
      </div>
    </div>
  );
}
